'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DarkModeContextType {
    darkMode: boolean;
    setDarkMode: (value: boolean) => void;
    toggleDarkMode: () => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

export function DarkModeProvider({ children }: { children: ReactNode }) {
    const [darkMode, setDarkModeState] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Load from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem('darkMode');
        if (stored !== null) {
            setDarkModeState(stored === 'true');
        }
        setMounted(true);
    }, []);

    // Save to localStorage when changed
    const setDarkMode = (value: boolean) => {
        setDarkModeState(value);
        localStorage.setItem('darkMode', String(value));
    };

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    // Always provide the context, even before mounting (to prevent "must be used within provider" error)
    return (
        <DarkModeContext.Provider value={{ darkMode, setDarkMode, toggleDarkMode }}>
            {children}
        </DarkModeContext.Provider>
    );
}

export function useDarkMode() {
    const context = useContext(DarkModeContext);
    if (context === undefined) {
        throw new Error('useDarkMode must be used within a DarkModeProvider');
    }
    return context;
}
